import React, { useState } from 'react';
import { Check, ArrowRight, Users, Clock, Package, Monitor, Building2, Lightbulb, Target, Sparkles, Video } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
type FormatType = 'inPerson' | 'digital';
export function ModernProductManagement() {
  const {
    t
  } = useLanguage();
  const [activeFormat, setActiveFormat] = useState<FormatType>('inPerson');
  const benefits = t('modernProductManagement.benefits') as string[];
  const forWhomItems = t('modernProductManagement.forWhomItems') as string[];
  const inPersonTiers = t('modernProductManagement.inPersonTiers') as {
    tier1: {
      name: string;
      duration: string;
      subtitle: string;
      description: string;
      features: string[];
    };
    tier2: {
      name: string;
      duration: string;
      subtitle: string;
      description: string;
      features: string[];
      highlighted?: boolean;
    };
    tier3: {
      name: string;
      duration: string;
      subtitle: string;
      description: string;
      features: string[];
    };
  };
  const digitalTiers = t('modernProductManagement.digitalTiers') as {
    tier1: {
      name: string;
      sessions: string;
      subtitle: string;
      description: string;
      features: string[];
    };
    tier2: {
      name: string;
      sessions: string;
      subtitle: string;
      description: string;
      features: string[];
      highlighted?: boolean;
    };
    tier3: {
      name: string;
      sessions: string;
      subtitle: string;
      description: string;
      features: string[];
    };
  };
  const formatToggle = t('modernProductManagement.formatToggle') as {
    inPerson: string;
    digital: string;
  };
  const forWhomIcons = [<Package className="text-amber-600 shrink-0" size={20} data-id="element-879" />, <Target className="text-amber-600 shrink-0" size={20} data-id="element-880" />, <Lightbulb className="text-amber-600 shrink-0" size={20} data-id="element-881" />, <Users className="text-amber-600 shrink-0" size={20} data-id="element-882" />];
  const renderTierCard = (tier: {
    name: string;
    duration?: string;
    sessions?: string;
    subtitle: string;
    description: string;
    features: string[];
    highlighted?: boolean;
  }, index: number) => {
    const isHighlighted = tier.highlighted;
    return <Card key={index} className={`p-0 overflow-hidden flex flex-col h-full ${isHighlighted ? 'ring-2 ring-amber-500 shadow-2xl shadow-amber-100 scale-[1.02]' : 'border border-gray-200'}`} data-id="element-883">
        {isHighlighted && <div className="bg-amber-600 text-white text-center py-2 text-sm font-bold" data-id="element-884">
            <Sparkles className="inline-block mr-2" size={16} data-id="element-885" />
            {activeFormat === 'inPerson' ? 'Vinsælast' : 'Vinsælast'}
          </div>}
        <div className="p-8 flex flex-col flex-1" data-id="element-886">
          <div className="mb-6" data-id="element-887">
            <span className="text-amber-600 font-bold text-sm uppercase tracking-wide" data-id="element-888">
              {tier.name}
            </span>
            <h3 className="text-2xl font-bold text-charcoal mt-1 mb-1" data-id="element-889">
              {tier.subtitle}
            </h3>
            <div className="flex items-center gap-2 text-gray-500 text-sm" data-id="element-890">
              {activeFormat === 'inPerson' ? <>
                  <Clock size={16} className="text-amber-600" data-id="element-891" />
                  <span data-id="element-892">{tier.duration}</span>
                </> : <>
                  <Video size={16} className="text-amber-600" data-id="element-893" />
                  <span data-id="element-894">{tier.sessions}</span>
                </>}
            </div>
          </div>

          <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-895">
            {tier.description}
          </p>

          <ul className="space-y-3 mb-8 flex-1" data-id="element-896">
            {tier.features.map((feature: string, i: number) => <li key={i} className="flex items-start gap-3" data-id="element-897">
                <div className="mt-0.5 p-1 rounded-full bg-amber-50 text-amber-600 flex-shrink-0" data-id="element-898">
                  <Check size={12} strokeWidth={3} data-id="element-899" />
                </div>
                <span className="text-gray-700 text-sm" data-id="element-900">{feature}</span>
              </li>)}
          </ul>

          <Link to="/contact" className="block mt-auto" data-id="element-901">
            <Button className={`w-full ${isHighlighted ? 'bg-amber-600 hover:bg-amber-700 shadow-amber-200' : ''}`} variant={isHighlighted ? 'primary' : 'outline'} rightIcon={<ArrowRight size={18} data-id="element-903" />} data-id="element-902">
              {t('modernProductManagement.inquiry')}
            </Button>
          </Link>
        </div>
      </Card>;
  };
  return <PageTransition data-id="element-904">
      <div className="pt-12 pb-24" data-id="element-905">
        {/* Hero Section */}
        <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-906">
          <DecorativeShape type="blob" color="bg-amber-100" className="top-0 right-0 w-[500px] h-[500px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-907" />
          <DecorativeShape type="circle" color="bg-orange-100" className="bottom-0 left-0 w-64 h-64 -translate-x-1/4 translate-y-1/4 -z-10" data-id="element-908" />

          <Animate className="text-center max-w-4xl mx-auto" data-id="element-909">
            <span className="inline-block px-4 py-2 bg-amber-50 text-amber-600 font-medium rounded-full text-sm mb-6" data-id="element-910">
              {t('modernProductManagement.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-911">
              {t('modernProductManagement.title')} <br data-id="element-912" />
              <span className="text-amber-600" data-id="element-913">
                {t('modernProductManagement.titleHighlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-914">
              {t('modernProductManagement.subtitle')}
            </p>

            <div className="flex flex-wrap justify-center gap-4 text-sm font-medium text-gray-500 mb-10" data-id="element-915">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-916">
                <Clock size={18} className="text-amber-600" data-id="element-917" />
                <span data-id="element-918">{t('modernProductManagement.duration')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-919">
                <Users size={18} className="text-amber-600" data-id="element-920" />
                <span data-id="element-921">{t('modernProductManagement.audience')}</span>
              </div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-922">
                <Monitor size={18} className="text-amber-600" data-id="element-923" />
                <span data-id="element-924">{t('modernProductManagement.format')}</span>
              </div>
            </div>

            <Link to="/contact" data-id="element-925">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white shadow-amber-200" size="lg" rightIcon={<ArrowRight size={20} data-id="element-927" />} data-id="element-926">
                {t('modernProductManagement.bookCta')}
              </Button>
            </Link>
          </Animate>
        </section>

        {/* About Section */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-928">
          <Animate data-id="element-929">
            <h2 className="text-3xl font-bold mb-6 text-charcoal text-center" data-id="element-930">
              {t('modernProductManagement.aboutTitle')}
            </h2>
            <div className="prose prose-lg text-gray-600 text-center max-w-3xl mx-auto" data-id="element-931">
              <p className="mb-4" data-id="element-932">{t('modernProductManagement.aboutP1')}</p>
              <p data-id="element-933">{t('modernProductManagement.aboutP2')}</p>
            </div>
          </Animate>
        </section>

        {/* What You Learn */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-934">
          <Animate data-id="element-935">
            <h3 className="text-2xl font-bold mb-8 text-charcoal text-center" data-id="element-936">
              {t('modernProductManagement.learnTitle')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-937">
              {benefits.map((benefit: string, index: number) => <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100" data-id="element-938">
                  <div className="mt-0.5 p-1.5 rounded-full bg-amber-50 text-amber-600 flex-shrink-0" data-id="element-939">
                    <Check size={14} strokeWidth={3} data-id="element-940" />
                  </div>
                  <span className="text-gray-700" data-id="element-941">{benefit}</span>
                </div>)}
            </div>
          </Animate>
        </section>

        {/* Format Toggle & Tiers */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-942">
          <Animate data-id="element-943">
            {/* Format Toggle */}
            <div className="flex justify-center mb-12" data-id="element-944">
              <div className="inline-flex bg-gray-100 rounded-full p-1.5" data-id="element-945">
                <button onClick={() => setActiveFormat('inPerson')} className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all ${activeFormat === 'inPerson' ? 'bg-white text-amber-600 shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-946">
                  <Building2 size={18} data-id="element-947" />
                  {formatToggle.inPerson}
                </button>
                <button onClick={() => setActiveFormat('digital')} className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all ${activeFormat === 'digital' ? 'bg-white text-amber-600 shadow-md' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-948">
                  <Monitor size={18} data-id="element-949" />
                  {formatToggle.digital}
                </button>
              </div>
            </div>

            {/* Tier Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch" data-id="element-950">
              {activeFormat === 'inPerson' ? <>
                  {renderTierCard(inPersonTiers.tier1, 0)}
                  {renderTierCard(inPersonTiers.tier2, 1)}
                  {renderTierCard(inPersonTiers.tier3, 2)}
                </> : <>
                  {renderTierCard(digitalTiers.tier1, 0)}
                  {renderTierCard(digitalTiers.tier2, 1)}
                  {renderTierCard(digitalTiers.tier3, 2)}
                </>}
            </div>

            {/* Digital Note */}
            {activeFormat === 'digital' && <Animate delay={0.1} data-id="element-951">
                <div className="mt-8 bg-amber-50 rounded-2xl p-6 text-center max-w-3xl mx-auto" data-id="element-952">
                  <Video className="mx-auto text-amber-600 mb-3" size={28} data-id="element-953" />
                  <p className="text-gray-700 leading-relaxed" data-id="element-954">
                    {t('modernProductManagement.digitalNote')}
                  </p>
                </div>
              </Animate>}
          </Animate>
        </section>

        {/* Sidebar-style section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-955">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-956">
            {/* For Whom */}
            <Animate data-id="element-957">
              <Card className="p-8 bg-white shadow-xl border-t-4 border-amber-600 h-full" data-id="element-958">
                <h3 className="text-xl font-bold mb-6 text-charcoal" data-id="element-959">
                  {t('modernProductManagement.forWhom')}
                </h3>
                <ul className="space-y-4 mb-8" data-id="element-960">
                  {forWhomItems.map((item: string, i: number) => <li key={i} className="flex items-start gap-3" data-id="element-961">
                      {forWhomIcons[i]}
                      <span className="text-gray-600" data-id="element-962">{item}</span>
                    </li>)}
                </ul>
                <Link to="/contact" className="block" data-id="element-963">
                  <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white shadow-amber-200" rightIcon={<ArrowRight size={18} data-id="element-965" />} data-id="element-964">
                    {t('modernProductManagement.inquiry')}
                  </Button>
                </Link>
              </Card>
            </Animate>

            {/* Group Offer */}
            <Animate delay={0.1} data-id="element-966">
              <div className="bg-amber-50 rounded-3xl p-8 text-center h-full flex flex-col justify-center" data-id="element-967">
                <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-md mb-4 text-amber-600" data-id="element-968">
                  <Users size={32} data-id="element-969" />
                </div>
                <h4 className="font-bold text-xl mb-3" data-id="element-970">
                  {t('modernProductManagement.groupTitle')}
                </h4>
                <p className="text-gray-600 mb-6" data-id="element-971">
                  {t('modernProductManagement.groupDesc')}
                </p>
                <Link to="/contact" className="text-amber-600 font-bold hover:underline text-lg" data-id="element-972">
                  {t('modernProductManagement.contactLink')} →
                </Link>
              </div>
            </Animate>
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-973">
          <Animate data-id="element-974">
            <Card className="p-12 bg-gradient-to-br from-amber-50 to-orange-50 border-0" data-id="element-975">
              <h2 className="text-3xl font-bold mb-4" data-id="element-976">
                {t('modernProductManagement.ctaTitle')}
              </h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-977">
                {t('modernProductManagement.ctaDesc')}
              </p>
              <Link to="/contact" data-id="element-978">
                <Button className="bg-amber-600 hover:bg-amber-700 text-white shadow-amber-200" rightIcon={<ArrowRight size={18} data-id="element-980" />} data-id="element-979">
                  {t('modernProductManagement.ctaCta')}
                </Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}